import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import productionAxios from '../../../../../redux/ProductionData/axiosConfig';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { 
  ArrowLeft, 
  FileSignature, 
  Loader2, 
  Plus, 
  Trash2,
  AlertCircle,
  CheckCircle,
  Download,
  ChevronDown,
  ChevronUp,
  FileText,
  ThumbsUp,
  ThumbsDown,
  Save
} from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { SignatureDialog } from '../../../../../components/ui/SignatureDialog';
import { useTeamMembers } from '../../../../../redux/ProductionData/hooks/useTeamMembers';
import { generateIRNReport, clearIRNReport } from '../../../../../redux/ProductionData/actions';
import { getMaterialReportVersions } from '../../../../../redux/Project/materialsData/action';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { useReportDraft } from '../../../../../redux/ReportDrafts/hooks/useReportDraft';
import { REPORT_TYPES } from '../../../../../redux/ReportDrafts/types';
import { ApproveButton } from '../../../../../components/ui/ApproveButton';
import { useProductionModalAudit } from '../../../../audit/useProductionModalAudit';
import { logIrnReportGenerated } from '../../../utils/productionAudit';

// Activity lists by IRN type
const ACTIVITIES_BY_TYPE: Record<string, string[]> = {
  pipe: [
    'Material Certification',
    'Dimensional',
    'Welding',
    'Visual Inspection',
    'Squareness & Alignment',
    'MPI',
    'MUT',
    'AUT',
    'Coating',
    'Shipping'
  ],
  material: [
    'Material Certification',
    'Dimensional',
    'Visual Inspection',
    'MPI',
    'MUT',
    'AUT'
  ],
  production: [
    'Material Certification',
    'Dimensional',
    'Welding',
    'Visual Inspection',
    'Hang Off Collar ID/OD Grinding',
    'MPI',
    'MUT',
    'AUT',
    'Coating',
    'Color Band Marking'
  ]
};

// Type labels for display
const TYPE_LABELS: Record<string, string> = {
  pipe: 'Pipe',
  material: 'Material',
  production: 'Production'
};

// Activity name to API key mapping
const ACTIVITY_TO_API_KEY: Record<string, string> = {
  'Material Certification': 'materialCertification',
  'Dimensional': 'dimensional',
  'Welding': 'welding',
  'Visual Inspection': 'visualInspection',
  'Squareness & Alignment': 'squarenessAlignment',
  'MPI': 'mpi',
  'MUT': 'mut',
  'AUT': 'aut',
  'Shipping': 'shipping',
  'Coating': 'coating',
 // 'Taper Cone': 'taperCone',
  // 'Hang Off Collar ID/OD Grinding': 'hangOffCollarGrinding',
  // 'Color Band Marking': 'colorBandMarking'
};

interface ActivityState {
  status: 'accepted' | 'rejected' | 'na' | null;
  comments: string;
}

interface FinalSignature {
  name: string;
  signature: string;
  timestamp: string;
  comments: string;
  stampImage?: string;
}

interface HeaderData {
  clientContractNo: string;
  description: string;
  location: string;
}

interface AssemblyOption {
  assemblyNumber: string;
}

interface ReleaseNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: number;
  projectName?: string;
  jobNumber?: string;
  // New props for Production integration
  initialType?: string;
  assemblies?: AssemblyOption[];
  selectedPipe?: any;
  hideTypeSelection?: boolean;
  
}

export const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  jobNumber,
  initialType,
  assemblies = [],
  selectedPipe,
  hideTypeSelection = false,
}) => {
  useProductionModalAudit(isOpen, 'Release Notes', projectId, projectName);
  const dispatch = useDispatch();

  // Get IRN report state from Redux
  const {
    irnReportLoading,
    irnReportSuccess,
    irnReportError,
    irnReportData
  } = useSelector((state: any) => state.productionData || {});

  // Get report versions state from Redux
  const {
    materialReportVersionsLoading,
    materialReportVersions,
    materialReportVersionsError
  } = useSelector((state: any) => state.materialsData || {});

  // Versions expand/collapse state
  const [isVersionsExpanded, setIsVersionsExpanded] = useState<boolean>(false);

  // PDF Viewer state
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);

  // Track if form has been modified (dirty state)
  const [isDirty, setIsDirty] = useState<boolean>(false);
  
  // Ref to track if we're currently applying a draft (to prevent isDirty from being set)
  const isApplyingDraftRef = React.useRef<boolean>(false);
  const prevIrnReportSuccessRef = React.useRef<boolean>(false);

  // Track if modal is reloading after save
  const [isReloading, setIsReloading] = useState<boolean>(false);

  // IRN Type selection - use initialType if provided
  const [selectedType, setSelectedType] = useState<string>(initialType || 'pipe');
  
  // Header fields
  const [headerData, setHeaderData] = useState<HeaderData>({
    clientContractNo: '',
    description: '',
    location: ''
  });

  // Serial Numbers (for all IRN types)
  const [serialNumbers, setSerialNumbers] = useState<string[]>(['']);
  
  // Selected assemblies (when assemblies prop is provided)
  const [selectedAssemblies, setSelectedAssemblies] = useState<string[]>([]);

  /** Full assembly list for pipe IRN from API (Redux only holds one table page, default 10). */
  const [fetchedPipeAssemblyOptions, setFetchedPipeAssemblyOptions] = useState<AssemblyOption[]>([]);
  const [pipeAssembliesFetchReady, setPipeAssembliesFetchReady] = useState(false);
  const [pipeAssembliesLoading, setPipeAssembliesLoading] = useState(false);

  // Selected activities (checkboxes)
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);

  // Activity states (only for selected activities)
  const [activityStates, setActivityStates] = useState<Record<string, ActivityState>>({});

  // Remarks
  const [remarks, setRemarks] = useState<string>('');

  // Final signatures
  const [ptQCSignature, setPtQCSignature] = useState<FinalSignature | null>(null);
  const [projectManagerSignature, setProjectManagerSignature] = useState<FinalSignature | null>(null);
  /** Per-activity client rep: team member id and signature (each activity row has its own panel). */
  const [activityCustomerRepMemberByActivity, setActivityCustomerRepMemberByActivity] = useState<
    Record<string, string>
  >({});
  const [activityCustomerRepSigByActivity, setActivityCustomerRepSigByActivity] = useState<
    Record<string, FinalSignature | null>
  >({});

  // Signature dialog state
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [signatureDialogContext, setSignatureDialogContext] = useState<{
    type: 'pt-qc' | 'project-manager' | 'customer-rep-activity';
    activity?: string;
    initialName?: string;
  } | null>(null);

  // Team members
  const { teamMembers, loadTeamMembers } = useTeamMembers();
  const [qcList, setQcList] = useState<Array<{ value: string; label: string; name: string }>>([]);
  const [pmList, setPmList] = useState<Array<{ value: string; label: string; name: string }>>([]);

  // Selected team members for final signatures
  const [selectedPtQCState, setSelectedPtQCState] = useState<string>('');
  const [selectedProjectManagerState, setSelectedProjectManagerState] = useState<string>('');
  // Handlers for selection changes that reset signatures
  const setSelectedPtQC = (value: string) => {
    if (selectedPtQCState !== value) {
      // If not applying draft (user manually changed dropdown), always clear signature
      // User needs to sign again for the new person
      if (!isApplyingDraftRef.current) {
        setPtQCSignature(null);
      } 
    }
    setSelectedPtQCState(value);
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const setActivityCustomerRepMember = (activity: string, value: string) => {
    setActivityCustomerRepMemberByActivity(prev => ({ ...prev, [activity]: value }));
    if (!isApplyingDraftRef.current) {
      setActivityCustomerRepSigByActivity(prev => ({ ...prev, [activity]: null }));
      setIsDirty(true);
    }
  };

  const setSelectedProjectManager = (value: string) => {
    if (selectedProjectManagerState !== value) {
      // If not applying draft (user manually changed dropdown), always clear signature
      // User needs to sign again for the new person
      if (!isApplyingDraftRef.current) {
        setProjectManagerSignature(null);
      } else {
        // When applying draft, only keep signature if it belongs to the newly selected person
        if (projectManagerSignature) {
          const selectedMember = pmList.find(m => m.value === value);
          // Clear signature if it doesn't match the newly selected person
          if (!selectedMember || projectManagerSignature.name !== selectedMember.name) {
            setProjectManagerSignature(null);
          }
        }
      }
    }
    setSelectedProjectManagerState(value);
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Use state values for reading
  const selectedPtQC = selectedPtQCState;
  const selectedProjectManager = selectedProjectManagerState;

  // Customer representative list for final signatures
  const [customerRepList, setCustomerRepList] = useState<Array<{ value: string; label: string; name: string }>>([]);

  // Loading state
  const [isGenerating, setIsGenerating] = useState(false);

  // Email state
  const [email, setEmail] = useState<string>('');
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string>('');

  // Draft management
  const {
    draft,
    draftSignatures,
    draftId,
    updatedAt: draftUpdatedAt,
    isReadyToGenerate,
    missingFields,
    isFetchingDraft,
    isSavingDraft,
    saveDraftSuccess,
    saveDraftError,
    saveDraft,
    loadDraft,
    clearDraft,
    clearSaveStatus
  } = useReportDraft();

  // Load team members when modal opens
  useEffect(() => {
    if (isOpen && projectId && teamMembers && teamMembers.length === 0) {
      loadTeamMembers(projectId);
    }
  }, [isOpen, projectId, loadTeamMembers, teamMembers]);

  // Load existing draft when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      loadDraft({
        projectId,
        reportType: REPORT_TYPES.INSPECTION_RELEASE_NOTE,
        pipeId: selectedPipe?.pipeId
      });
    }
  }, [isOpen, projectId, selectedPipe?.pipeId, loadDraft]);

  useEffect(() => {
    if (!isOpen) {
      setFetchedPipeAssemblyOptions([]);
      setPipeAssembliesFetchReady(false);
      setPipeAssembliesLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !hideTypeSelection || !projectId || !selectedPipe?.pipeId) {
      return;
    }

    let cancelled = false;
    setPipeAssembliesFetchReady(false);
    setPipeAssembliesLoading(true);

    const mapProductionsToOptions = (list: any[]): AssemblyOption[] =>
      list.map((assembly: any) => ({
        assemblyNumber:
          assembly.assemblyNumber ||
          assembly.assembly_number ||
          `Assembly-${assembly.production_id}`,
      }));

    const loadAll = async () => {
      try {
        // Matches ProductionData saga: offset is 0-based page index, limit is page size.
        const pageSize = 10;
        const accumulated: AssemblyOption[] = [];
        let pageIndex = 0;
        let totalCount: number | undefined;

        while (!cancelled) {
          const params = new URLSearchParams({
            project_id: String(projectId),
            pipe_id: String(selectedPipe.pipeId),
            offset: String(pageIndex),
            limit: String(pageSize),
          });
          const res = await productionAxios.get(`/productions/getAllProductions?${params.toString()}`);
          if (cancelled) return;
          const data = get(res, ['data', 'data'], {});
          const list = data?.productions ?? [];
          if (totalCount == null && data?.totalCount != null) {
            totalCount = Number(data.totalCount);
          }

          accumulated.push(...mapProductionsToOptions(list));
       
          const doneByTotal =
            totalCount != null &&
            !Number.isNaN(totalCount) &&
            accumulated.length >= totalCount;
          const doneByShortPage = list.length < pageSize;
          const doneByEmpty = list.length === 0;

          if (doneByEmpty || doneByShortPage || doneByTotal) {
            break;
          }

          pageIndex += 1;
          const safetyCap = 10000;
          if (pageIndex >= safetyCap) {
            console.warn(
              '[ReleaseNotesModal] Stopped assembly fetch after safety cap of pages:',
              safetyCap
            );
            break;
          }
        }
        if (!cancelled) {
          if (selectedPipe?.numberOfBuckleArrestors) {
            const maxBuckle = selectedPipe.numberOfBuckleArrestors;
            const filtered = accumulated.filter((_assembly: any, index: number) => index < maxBuckle);
            setFetchedPipeAssemblyOptions(filtered);
          }
        }

      } catch {
        if (!cancelled) {
          setFetchedPipeAssemblyOptions([]);
        }
      } finally {
        if (!cancelled) {
          setPipeAssembliesFetchReady(true);
          setPipeAssembliesLoading(false);
        }
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [isOpen, hideTypeSelection, projectId, selectedPipe?.pipeId]);

  const assemblyOptionsForUi = useMemo(() => {
    if (hideTypeSelection && selectedPipe?.pipeId && projectId) {
      if (!pipeAssembliesFetchReady) {
        return [];
      }
      if (fetchedPipeAssemblyOptions.length > 0) {
        return fetchedPipeAssemblyOptions;
      }
    }
    return assemblies;
  }, [
    assemblies,
    fetchedPipeAssemblyOptions,
    hideTypeSelection,
    pipeAssembliesFetchReady,
    projectId,
    selectedPipe?.pipeId,
  ]);

  // Handle applying draft to form
  const handleApplyDraft = useCallback(() => {
    if (!draft) return;

    // Mark that we're applying draft to prevent signature resets
    isApplyingDraftRef.current = true;

    // Populate header data
    if (draft.headerData) {
      setHeaderData({
        clientContractNo: draft.headerData.clientContractNo || '',
        description: draft.headerData.description || '',
        location: draft.headerData.location || ''
      });
    }

    // Populate selected type
    if (draft.selectedType) {
      setSelectedType(draft.selectedType);
    }

    // Populate serial numbers
    if (draft.serialNumbers && draft.serialNumbers.length > 0) {
      setSerialNumbers(draft.serialNumbers);
    }

    // Populate selected assemblies
    if (draft.selectedAssemblies && draft.selectedAssemblies.length > 0) {
      setSelectedAssemblies(draft.selectedAssemblies);
    }

    // Populate selected activities
    if (draft.selectedActivities && draft.selectedActivities.length > 0) {
      setSelectedActivities(draft.selectedActivities);
    }

    // Populate activity states
    if (draft.activityStates) {
      setActivityStates(draft.activityStates);
    }

    // Populate remarks
    if (draft.remarks) {
      setRemarks(draft.remarks);
    }

    // Populate signatures from draftSignatures (stored separately)
    // Merge with existing signatures to preserve current signatures if they exist
    if (draftSignatures) {
      // Only apply draft QC signature if current signature doesn't exist or is empty
      if (draftSignatures.qc && draftSignatures.qc.signed) {
        const currentQcHasSignature = ptQCSignature?.signature && ptQCSignature.signature.trim();
        
        if (!currentQcHasSignature) {
          setPtQCSignature({
            name: draftSignatures.qc.name || '',
            signature: draftSignatures.qc.signature || '',
            timestamp: draftSignatures.qc.timestamp || '',
            comments: draftSignatures.qc.comment || '',
            stampImage: draftSignatures.qc.stampImage || ''
          });
        }
        
        // Always try to restore selection if teamMemberId exists, even if signature already exists
        const teamMemberId = draftSignatures.qc.teamMemberId?.toString();
        if (teamMemberId && qcList.length > 0) {
          const matchingQc = qcList.find(m => m.value === teamMemberId);
          if (matchingQc && selectedPtQC !== matchingQc.value) {
            const currentSelectionIsValid = selectedPtQC && qcList.find(m => m.value === selectedPtQC);
            if (!currentSelectionIsValid || selectedPtQC === 'draft-restored') {
              setSelectedPtQC(matchingQc.value);
            }
          }
        }
      }

      // Per-activity client rep signatures (new format) or legacy customer / customerWelding / customerCoating
      const irnActs = ACTIVITIES_BY_TYPE[draft.selectedType || selectedType] || [];
      const nextMembers: Record<string, string> = {};
      const nextSigs: Record<string, FinalSignature | null> = {};
      irnActs.forEach(a => {
        nextMembers[a] = '';
        nextSigs[a] = null;
      });

      const acr = draftSignatures.activityCustomerReps as Record<string, any> | undefined;
      if (acr && typeof acr === 'object') {
        irnActs.forEach(act => {
          const e = acr[act];
          if (e?.signed && e.signature) {
            nextSigs[act] = {
              name: e.name || '',
              signature: e.signature || '',
              timestamp: e.timestamp || '',
              comments: e.comment || '',
              stampImage: e.stampImage || '',
            };
          }
          if (e?.teamMemberId != null && e.teamMemberId !== '') {
            nextMembers[act] = String(e.teamMemberId);
          }
        });
      } else {
        const toFinal = (e: any): FinalSignature | null =>
          e?.signed && e.signature
            ? {
                name: e.name || '',
                signature: e.signature || '',
                timestamp: e.timestamp || '',
                comments: e.comment || '',
                stampImage: e.stampImage || '',
              }
            : null;
        const cust = draftSignatures.customer;
        const genSig = toFinal(cust);
        const genId = cust?.teamMemberId != null ? String(cust.teamMemberId) : '';
        if (genSig || genId) {
          irnActs.forEach(a => {
            if (a !== 'Welding' && a !== 'Coating') {
              if (genSig) nextSigs[a] = genSig;
              if (genId) nextMembers[a] = genId;
            }
          });
        }
        const wSig = toFinal(draftSignatures.customerWelding);
        const wId =
          draftSignatures.customerWelding?.teamMemberId != null
            ? String(draftSignatures.customerWelding.teamMemberId)
            : '';
        if (wSig || wId) {
          if (irnActs.includes('Welding')) {
            if (wSig) nextSigs['Welding'] = wSig;
            if (wId) nextMembers['Welding'] = wId;
          }
        }
        const cSig = toFinal(draftSignatures.customerCoating);
        const cId =
          draftSignatures.customerCoating?.teamMemberId != null
            ? String(draftSignatures.customerCoating.teamMemberId)
            : '';
        if (cSig || cId) {
          if (irnActs.includes('Coating')) {
            if (cSig) nextSigs['Coating'] = cSig;
            if (cId) nextMembers['Coating'] = cId;
          }
        }
      }

      if (draft.activityCustomerRepMembers && typeof draft.activityCustomerRepMembers === 'object') {
        irnActs.forEach(a => {
          const v = (draft.activityCustomerRepMembers as Record<string, string>)[a];
          if (v) nextMembers[a] = v;
        });
      }

      setActivityCustomerRepMemberByActivity(nextMembers);
      setActivityCustomerRepSigByActivity(nextSigs);

      // Only apply draft projectManager signature if current signature doesn't exist or is empty
      if (draftSignatures.projectManager && draftSignatures.projectManager.signed) {
        const currentPmHasSignature = projectManagerSignature?.signature && projectManagerSignature.signature.trim();
        
        if (!currentPmHasSignature) {
          setProjectManagerSignature({
            name: draftSignatures.projectManager.name || '',
            signature: draftSignatures.projectManager.signature || '',
            timestamp: draftSignatures.projectManager.timestamp || '',
            comments: draftSignatures.projectManager.comment || '',
            stampImage: draftSignatures.projectManager.stampImage || ''
          });
        }
        
        // Always try to restore selection if teamMemberId exists, even if signature already exists
        const teamMemberId = draftSignatures.projectManager.teamMemberId?.toString();
        if (teamMemberId && pmList.length > 0) {
          const matchingPm = pmList.find(m => m.value === teamMemberId);
          if (matchingPm && selectedProjectManager !== matchingPm.value) {
            const currentSelectionIsValid = selectedProjectManager && pmList.find(m => m.value === selectedProjectManager);
            if (!currentSelectionIsValid || selectedProjectManager === 'draft-restored') {
              setSelectedProjectManager(matchingPm.value);
            }
          }
        }
      }
    }

    // Reset dirty state after applying draft (use setTimeout to ensure all state updates complete)
    isApplyingDraftRef.current = true;
    setTimeout(() => {
      setIsDirty(false);
      isApplyingDraftRef.current = false;
    }, 100);
    // Hide loader after draft is applied
    setIsReloading(false);
  }, [draft, draftSignatures, qcList, customerRepList, pmList]);

  // Auto-apply draft when it's loaded
  useEffect(() => {
    if (draft && !isFetchingDraft) {
      // Clear reloading state when draft is successfully loaded
      setIsReloading(false);
      handleApplyDraft();
    }
  }, [draft, isFetchingDraft, handleApplyDraft]);

  // Reload draft after successful save to get fresh data from server
  useEffect(() => {
    if (saveDraftSuccess && projectId) {
      setIsDirty(false);
      // Clear save status to prevent re-triggering
      clearSaveStatus();
      // Show subtle reload indicator (doesn't block interaction)
      setIsReloading(true);
      // Reload draft from server to get fresh data including stamp images
      // This ensures we have the latest data with all IDs and stamp images
      loadDraft({
        projectId,
        reportType: REPORT_TYPES.INSPECTION_RELEASE_NOTE,
        pipeId: selectedPipe?.pipeId
      });
    }
  }, [saveDraftSuccess, projectId, loadDraft, clearSaveStatus, selectedPipe]);

  // Auto-open PDF viewer when IRN report generates successfully (only when modal is open)
  useEffect(() => {
    if (!irnReportSuccess) {
      prevIrnReportSuccessRef.current = false;
      return;
    }
    const justSucceeded = !prevIrnReportSuccessRef.current;
    prevIrnReportSuccessRef.current = true;

    if (isOpen && irnReportData) {
      const fileUrl = irnReportData?.data?.downloadUrl || irnReportData?.downloadUrl;
      const fileName = irnReportData?.data?.fileName || irnReportData?.fileName || 'IRN Report';
      if (fileUrl) {
        setViewerFile({ fileUrl, fileName });
      }
      if (justSucceeded) {
        setIsDirty(false);
        const fileUrl = irnReportData?.data?.downloadUrl || irnReportData?.downloadUrl;
        const fileName = irnReportData?.data?.fileName || irnReportData?.fileName || null;
        logIrnReportGenerated({
          projectId,
          projectName,
          irnType: selectedType,
          assemblyNumbers: selectedAssemblies,
          downloadUrl: fileUrl ?? null,
          fileName,
        });
      }
    }
  }, [isOpen, irnReportSuccess, irnReportData, selectedType, selectedAssemblies, projectId, projectName]);

  
  // Process team members into lists
  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      const qcInspectors = teamMembers
        .filter((member: any) => 
          member?.team_member_role === 'Quality Control' || 
          member?.team_member_role === 'QC' || 
          member?.team_member_role === 'QA/QC'
        )
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`
        }));

      const projectManagers = teamMembers
        .filter((member: any) => 
          member?.team_member_role === 'Project Manager' || 
          member?.team_member_role === 'Manager' ||
          member?.projectLevelRole?.project_role_name === 'Manager' ||
          member?.projectLevelRole?.project_role_name === 'Project Manager'
        )
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`
        }));
      const customerReps = teamMembers
        .filter((member: any) => 
          member?.team_member_role === 'Customer Representative' || 
          member?.team_member_role === 'Client Representative' ||
          member?.team_member_role === 'Inspector'
        )
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`
        }));

      setQcList(qcInspectors);
      setPmList(projectManagers);
      setCustomerRepList(customerReps);
    }
  }, [teamMembers]);

  // Restore dropdown selections from draft when team member lists are populated
  useEffect(() => {
    if (!draftSignatures || isApplyingDraftRef.current) return;

    // Restore QC Inspector selection
    if (draftSignatures.qc && draftSignatures.qc.teamMemberId && qcList.length > 0) {
      const teamMemberId = draftSignatures.qc.teamMemberId.toString();
      const matchingQc = qcList.find(m => m.value === teamMemberId);
      if (matchingQc && selectedPtQC !== matchingQc.value) {
        // Only restore if not already set to a valid value, or if set to invalid value
        const currentSelectionIsValid = selectedPtQC && qcList.find(m => m.value === selectedPtQC);
        if (!currentSelectionIsValid || selectedPtQC === 'draft-restored') {
          isApplyingDraftRef.current = true;
          setSelectedPtQCState(matchingQc.value);
          setTimeout(() => {
            isApplyingDraftRef.current = false;
          }, 50);
        }
      }
    }

    // Restore Project Manager selection
    if (draftSignatures.projectManager && draftSignatures.projectManager.teamMemberId && pmList.length > 0) {
      const teamMemberId = draftSignatures.projectManager.teamMemberId.toString();
      const matchingPm = pmList.find(m => m.value === teamMemberId);
      if (matchingPm && selectedProjectManager !== matchingPm.value) {
        // Only restore if not already set to a valid value, or if set to invalid value
        const currentSelectionIsValid = selectedProjectManager && pmList.find(m => m.value === selectedProjectManager);
        if (!currentSelectionIsValid || selectedProjectManager === 'draft-restored') {
          isApplyingDraftRef.current = true;
          setSelectedProjectManagerState(matchingPm.value);
          setTimeout(() => {
            isApplyingDraftRef.current = false;
          }, 50);
        }
      }
    }
  }, [draftSignatures, qcList, customerRepList, pmList, selectedPtQC, selectedProjectManager]);

  // Format project sub type for display
  const formatSubType = (type: string): string => {
    switch (type) {
      case 'buckleArrestor': return 'Buckle Arrestor';
      case 'pipeInPipe': return 'Pipe in Pipe';
      case 'bulkhead': return 'Bulk Head';
      case 'none': return '';
      default: return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
    }
  };

  // Initialize activity states and reset selected activities when type changes
  useEffect(() => {
    const activities = ACTIVITIES_BY_TYPE[selectedType] || [];
    const initialStates: Record<string, ActivityState> = {};
    
    activities.forEach(activity => {
      initialStates[activity] = {
        status: null,
        comments: ''
      };
    });
    
    setActivityStates(initialStates);
    setSelectedActivities([]);
    const initMembers: Record<string, string> = {};
    const initSigs: Record<string, FinalSignature | null> = {};
    activities.forEach(a => {
      initMembers[a] = '';
      initSigs[a] = null;
    });
    setActivityCustomerRepMemberByActivity(initMembers);
    setActivityCustomerRepSigByActivity(initSigs);
  }, [selectedType]);

  // Reset form when modal closes or initialize when opens
  useEffect(() => {
    if (!isOpen) {
      setSelectedType(initialType || 'pipe');
      setHeaderData({
        clientContractNo: '',
        description: '',
        location: ''
      });
      setSerialNumbers(['']);
      setSelectedAssemblies([]);
      setSelectedActivities([]);
      setActivityStates({});
      setRemarks('');
      setPtQCSignature(null);
      setProjectManagerSignature(null);
      setActivityCustomerRepMemberByActivity({});
      setActivityCustomerRepSigByActivity({});
      setSelectedPtQC('');
      setSelectedProjectManager('');
      // Reset email state
      setEmail('');
      setEmailList([]);
      setEmailError('');
      // Reset versions state
      setIsVersionsExpanded(false);
      setViewerFile(null);
      setIsDirty(false);
      // Clear draft state
      clearDraft();
      // Clear IRN report state to prevent auto-opening viewer on next open
      dispatch(clearIRNReport());
    } else {
      // When opening: clear viewer + IRN report state so a prior success/error cannot auto-open the PDF viewer
      setViewerFile(null);
      prevIrnReportSuccessRef.current = false;
      dispatch(clearIRNReport());

      // When opening, set initial values
      if (initialType) {
        setSelectedType(initialType);
      }

      // Auto-generate description from selectedPipe data
      if (selectedPipe) {
        const size = selectedPipe.size || selectedPipe.pipeSize || '';
        const wallThickness = selectedPipe.wallThickness || '';
        const subType = selectedPipe.projectSubType || '';
        const formattedSubType = formatSubType(subType);
        if (size && wallThickness) {
          const autoDescription = `${size}"O.D.x ${wallThickness}" W.T${formattedSubType ? ` ${formattedSubType}` : ''}`;
          setHeaderData(prev => ({ ...prev, description: autoDescription }));
        }
      }
      // Fetch IRN report versions
    //   const effectivePipeId = selectedPipe?.pipeId 
    //   if (effectivePipeId) {
    //     dispatch(getMaterialReportVersions(effectivePipeId, 'INSPECTION_RELEASE_NOTE'));
    // }
    }
  }, [isOpen, initialType, dispatch, selectedPipe, clearDraft]);

  // Handle save draft
  const handleSaveDraft = useCallback(() => {
    const effectivePipeId = selectedPipe?.pipeId 

    // Build signatures object - always include all keys to properly handle removals
    // Check for signatures in both current state and draftSignatures (in case state hasn't updated yet)
    const currentQcSignature = ptQCSignature;
    const currentPmSignature = projectManagerSignature;
    
    const draftQcSignature = draftSignatures?.qc;
    const draftPmSignature = draftSignatures?.projectManager;
    const draftAcr = draftSignatures?.activityCustomerReps as Record<string, any> | undefined;
    
    const qcSignature = (currentQcSignature?.signature && currentQcSignature.signature.trim()) 
      ? currentQcSignature 
      : (draftQcSignature?.signed && draftQcSignature?.signature) ? {
          name: draftQcSignature.name || '',
          signature: draftQcSignature.signature || '',
          stampImage: draftQcSignature.stampImage || '',
          timestamp: draftQcSignature.timestamp || '',
          comments: draftQcSignature.comment || ''
        } : null;

    const typeActs = ACTIVITIES_BY_TYPE[selectedType] || [];
    const activityCustomerReps: Record<string, any> = {};
    typeActs.forEach(act => {
      const cur = activityCustomerRepSigByActivity[act];
      const curOk = cur?.signature && cur.signature.trim();
      const dr = draftAcr?.[act];
      const drOk = dr?.signed && dr?.signature;
      const sig: FinalSignature | null = curOk
        ? cur
        : drOk
          ? {
              name: dr.name || '',
              signature: dr.signature || '',
              timestamp: dr.timestamp || '',
              comments: dr.comment || '',
              stampImage: dr.stampImage || '',
            }
          : null;
      const teamIdRaw =
        activityCustomerRepMemberByActivity[act] ||
        (dr?.teamMemberId != null && dr.teamMemberId !== '' ? String(dr.teamMemberId) : '');
      if (sig) {
        activityCustomerReps[act] = {
          name: sig.name || '',
          signature: sig.signature || '',
          stampImage: sig.stampImage || '',
          timestamp: sig.timestamp || new Date().toISOString(),
          signed: true,
          comment: sig.comments || '',
          teamMemberId: teamIdRaw || null,
        };
      } else {
        activityCustomerReps[act] = { signed: false, teamMemberId: teamIdRaw || null };
      }
    });

    const packLegacyCustomer = (e: any) =>
      e?.signed
        ? {
            name: e.name || '',
            signature: e.signature || '',
            stampImage: e.stampImage || '',
            timestamp: e.timestamp || new Date().toISOString(),
            signed: true,
            comment: e.comment || '',
            teamMemberId: e.teamMemberId ?? null,
          }
        : { signed: false };

    const firstGeneralSigned = typeActs.find(
      a => a !== 'Welding' && a !== 'Coating' && activityCustomerReps[a]?.signed
    );

    const pmSignature = (currentPmSignature?.signature && currentPmSignature.signature.trim())
      ? currentPmSignature
      : (draftPmSignature?.signed && draftPmSignature?.signature) ? {
          name: draftPmSignature.name || '',
          signature: draftPmSignature.signature || '',
          stampImage: draftPmSignature.stampImage || '',
          timestamp: draftPmSignature.timestamp || '',
          comments: draftPmSignature.comment || ''
        } : null;

    const signatures: Record<string, any> = {
      qc: qcSignature ? {
        name: qcSignature.name || '',
        signature: qcSignature.signature || '',
        stampImage: qcSignature.stampImage || '',
        timestamp: qcSignature.timestamp || new Date().toISOString(),
        signed: true,
        comment: qcSignature.comments || '',
        teamMemberId: selectedPtQC || draftQcSignature?.teamMemberId || null
      } : { signed: false },
      activityCustomerReps,
      customer: packLegacyCustomer(
        firstGeneralSigned ? activityCustomerReps[firstGeneralSigned] : { signed: false }
      ),
      customerWelding: packLegacyCustomer(activityCustomerReps['Welding']),
      customerCoating: packLegacyCustomer(activityCustomerReps['Coating']),
      projectManager: pmSignature ? {
        name: pmSignature.name || '',
        signature: pmSignature.signature || '',
        stampImage: pmSignature.stampImage || '',
        timestamp: pmSignature.timestamp || new Date().toISOString(),
        signed: true,
        comment: pmSignature.comments || '',
        teamMemberId: selectedProjectManager || draftPmSignature?.teamMemberId || null
      } : { signed: false }
    };

    // Build form data object
    const formData = {
      selectedType,
      headerData,
      serialNumbers: serialNumbers.filter(s => s.trim()),
      selectedAssemblies,
      selectedActivities,
      activityStates,
      remarks,
      emailList,
      activityCustomerRepMembers: activityCustomerRepMemberByActivity,
    };

    // Build save draft payload
    const saveDraftPayload: any = {
      projectId,
      reportType: REPORT_TYPES.INSPECTION_RELEASE_NOTE,
      pipeId: effectivePipeId,
      projectTypeId: selectedPipe?.projectTypeId,
      formData,
      signatures
    };

    // Include draftId if it exists (for updating existing draft)
    if (draftId) {
      saveDraftPayload.draftId = draftId;
    }

    // Call save draft
    saveDraft(saveDraftPayload);
  }, [
    projectId,
    selectedPipe,
    selectedType,
    headerData,
    serialNumbers,
    selectedAssemblies,
    selectedActivities,
    activityStates,
    remarks,
    emailList,
    ptQCSignature,
    activityCustomerRepMemberByActivity,
    activityCustomerRepSigByActivity,
    projectManagerSignature,
    saveDraft,
    selectedPtQC,
    selectedProjectManager,
    draftId,
    draftSignatures
  ]);

  // Handle header field changes
  const handleHeaderChange = (field: keyof HeaderData, value: string | number) => {
    setHeaderData(prev => ({ ...prev, [field]: value }));
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Handle serial number changes
  const handleSerialNumberChange = (index: number, value: string) => {
    setSerialNumbers(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const addSerialNumber = () => {
    setSerialNumbers(prev => [...prev, '']);
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const removeSerialNumber = (index: number) => {
    setSerialNumbers(prev => prev.filter((_, i) => i !== index));
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Handle assembly selection (when assemblies prop is provided)
  const handleAssemblyToggle = (assemblyNumber: string) => {
    setSelectedAssemblies(prev => {
      if (prev.includes(assemblyNumber)) {
        return prev.filter(a => a !== assemblyNumber);
      } else {
        return [...prev, assemblyNumber];
      }
    });
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const handleSelectAllAssemblies = () => {
    if (selectedAssemblies.length === assemblyOptionsForUi.length) {
      setSelectedAssemblies([]);
    } else {
      setSelectedAssemblies(assemblyOptionsForUi.map(a => a.assemblyNumber));
    }
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Handle activity selection (checkbox)
  const handleActivityToggle = (activity: string) => {
    setSelectedActivities(prev => {
      if (prev.includes(activity)) {
        return prev.filter(a => a !== activity);
      } else {
        return [...prev, activity];
      }
    });
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  const handleSelectAllActivities = () => {
    const activities = ACTIVITIES_BY_TYPE[selectedType] || [];
    if (selectedActivities.length === activities.length) {
      setSelectedActivities([]);
    } else {
      setSelectedActivities([...activities]);
    }
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Handle activity status (can be 'accepted', 'rejected', 'na', or null to deselect)
  const handleActivityStatus = (activity: string, status: 'accepted' | 'rejected' | 'na' | null) => {
    // Auto-select the activity if not already selected when clicking Accept/Reject/NA
    if (status !== null && !selectedActivities.includes(activity)) {
      setSelectedActivities(prev => [...prev, activity]);
    }
    
    setActivityStates(prev => ({
      ...prev,
      [activity]: {
        ...prev[activity],
        status
      }
    }));
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Handle activity comment change
  const handleActivityCommentChange = (activity: string, comments: string) => {
    // Auto-select the activity if not already selected when typing in comments
    if (!selectedActivities.includes(activity)) {
      setSelectedActivities(prev => [...prev, activity]);
    }
    
    setActivityStates(prev => ({
      ...prev,
      [activity]: {
        ...prev[activity],
        comments
      }
    }));
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Email handlers
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError('');
  };

  const handleAddEmail = () => {
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (emailList.includes(trimmedEmail)) {
      setEmailError('This email is already added');
      return;
    }

    setEmailList([...emailList, trimmedEmail]);
    setEmail('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmailList(emailList.filter(e => e !== emailToRemove));
  };

  const handleEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !irnReportLoading) {
      e.preventDefault();
      if (email.trim()) {
        handleAddEmail();
      }
    }
  };

  // Process versions - sort by versionNumber descending, mark latest
  const processedVersions = useMemo(() => {
    if (!materialReportVersions || !Array.isArray(materialReportVersions) || materialReportVersions.length === 0) {
      return { latest: null, older: [] };
    }

    // Sort by versionNumber descending (latest first)
    const sortedVersions = [...materialReportVersions].sort((a: any, b: any) => {
      const versionA = a.details?.versionNumber || a.versionNumber || 0;
      const versionB = b.details?.versionNumber || b.versionNumber || 0;
      return versionB - versionA;
    });

    const latest = sortedVersions[0] || null;
    const older = sortedVersions.slice(1);

    return { latest, older };
  }, [materialReportVersions]);

  // Handle download/view
  const handleDownloadReport = (doc: any) => {
    if (!doc || !(doc.fileUrl || doc.file_url)) {
      return;
    }
    setViewerFile({
      fileUrl: doc.fileUrl || doc.file_url,
      fileName: doc.fileName || doc.file_name || 'IRN Report'
    });
  };

  // Close viewer
  const closeViewer = () => {
    
    setViewerFile(null);
  };

  // Open signature dialog for final signatures
  const openFinalSignatureDialog = (type: 'pt-qc' | 'project-manager') => {
    let selectedMember;
    if (type === 'pt-qc') {
      selectedMember = qcList.find(m => m.value === selectedPtQC);
    } else {
      selectedMember = pmList.find(m => m.value === selectedProjectManager);
    }

    setSignatureDialogContext({
      type,
      initialName: selectedMember?.name || ''
    });
    setIsSignatureDialogOpen(true);
  };

  const openActivityCustomerRepSignatureDialog = (activity: string) => {
    const mid = activityCustomerRepMemberByActivity[activity] || '';
    const selectedMember = customerRepList.find(m => m.value === mid);
    setSignatureDialogContext({
      type: 'customer-rep-activity',
      activity,
      initialName: selectedMember?.name || ''
    });
    setIsSignatureDialogOpen(true);
  };

  // Handle signature save
  const handleSignatureSave = (signature: string, name: string, comment?: string, stampImage?: string) => {
    if (!signatureDialogContext) return;

    const signatureData = {
      signature,
      name,
      timestamp: new Date().toISOString(),
      comments: comment || '',
      stampImage: stampImage || ''
    };

    if (signatureDialogContext.type === 'pt-qc') {
      setPtQCSignature(signatureData);
    } else if (signatureDialogContext.type === 'project-manager') {
      setProjectManagerSignature(signatureData);
    } else if (signatureDialogContext.type === 'customer-rep-activity' && signatureDialogContext.activity) {
      const act = signatureDialogContext.activity;
      setActivityCustomerRepSigByActivity(prev => ({ ...prev, [act]: signatureData }));
    }

    setIsSignatureDialogOpen(false);
    setSignatureDialogContext(null);
    if (!isApplyingDraftRef.current) {
      setIsDirty(true);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Compute header info items for display (project, job, pipe info)
  const headerInfoItems = useMemo(() => {
    const infoItems = [];
    
    // Add project name
    if (projectName) {
      infoItems.push({ label: 'Project', value: projectName, bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]' });
    }
    
    // Add job number
    if (jobNumber) {
      infoItems.push({ label: 'Job', value: jobNumber, bgColor: 'bg-cyan-100 text-cyan-800' });
    }
    
    // Add pipe information if available
    if (selectedPipe) {
      const size = selectedPipe.size || selectedPipe.pipeSize || '';
      const wallThickness = selectedPipe.wallThickness || '';
      const subType = selectedPipe.projectSubType || '';
      const formattedSubType = formatSubType(subType);
      
      if (size) {
        infoItems.push({ label: 'Size', value: `${size}" O.D.`, bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]' });
      }
      if (wallThickness) {
        infoItems.push({ label: 'Wall Thickness', value: `${wallThickness}" W.T`, bgColor: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]' });
      }
      if (formattedSubType) {
        infoItems.push({ label: 'Type', value: formattedSubType, bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]' });
      }
    }
    
    return infoItems;
  }, [projectName, jobNumber, selectedPipe]);

  // Check if form is valid for generation
  const isFormValid = () => {
    // Check header fields
    if (!headerData.clientContractNo || !headerData.description || !headerData.location) {
      return false;
    }

    // Check serial numbers
    // If assemblies are provided, check selectedAssemblies, otherwise check serialNumbers
    if (assemblyOptionsForUi.length > 0) {
      if (selectedAssemblies.length === 0) return false;
    } else {
      if (serialNumbers.filter(s => s.trim()).length === 0) return false;
    }

    // Check at least one activity is selected
    if (selectedActivities.length === 0) {
      return false;
    }

    // // Check all selected activities have status set
    // for (const activity of selectedActivities) {
    //   const state = activityStates[activity];
    //   if (!state || state.status === null) {
    //     return false;
    //   }
    // }

    if (!ptQCSignature?.signature?.trim() || !projectManagerSignature?.signature?.trim()) {
    //  console.log('ptQCSignature?.signature?.trim()', ptQCSignature?.signature?.trim());
      return false;
    }
// console.log('activityCustomerRepSigByActivity', activityCustomerRepSigByActivity);
// console.log('selectedActivities', selectedActivities);
    // Each checked (selected) activity must have its own client representative signature; unchecked activities are ignored.
    const hasClientRepSig = (s: FinalSignature | null | undefined) =>
      !!(s?.signature && s.signature.trim());
    const everySelectedHasClientRepSig = selectedActivities.every(act =>
      hasClientRepSig(activityCustomerRepSigByActivity[act])
    );
    if (!everySelectedHasClientRepSig) {
    //  console.log('everySelectedHasClientRepSig', everySelectedHasClientRepSig);
      return false;
    }

    return true;
  };

  // Check if form has minimum required fields for saving draft
  const isDraftValid = () => {
    // Check required header fields
    if (!headerData.clientContractNo?.trim() || !headerData.location?.trim()) {
      return false;
    }

    // Check at least one assembly/serial number is selected
    if (assemblyOptionsForUi.length > 0) {
      if (selectedAssemblies.length === 0) return false;
    } else {
      if (serialNumbers.filter(s => s.trim()).length === 0) return false;
    }

    // Check at least one activity is selected
    if (selectedActivities.length === 0) {
      return false;
    }

    return true;
  };

  // Generate when fields, selected activities, QC + PM signatures, and a client rep signature per selected activity are present.
  const canGenerate = isFormValid();

  // Get tooltip message for generate button based on current state
  const getGenerateButtonTooltip = () => {
    const hasClientRepSig = (s: FinalSignature | null | undefined) =>
      !!(s?.signature && s.signature.trim());

    if (!headerData.clientContractNo || !headerData.description || !headerData.location) {
      return 'Complete client contract no, description, and location';
    }
    if (assemblyOptionsForUi.length > 0) {
      if (selectedAssemblies.length === 0) return 'Select at least one assembly';
    } else if (serialNumbers.filter(s => s.trim()).length === 0) {
      return 'Enter at least one serial number';
    }
    if (selectedActivities.length === 0) {
      return 'Select at least one activity';
    }
    if (!ptQCSignature?.signature?.trim()) {
      return 'QC Inspector signature required to generate';
    }
    if (!projectManagerSignature?.signature?.trim()) {
      return 'Project Manager signature required to generate';
    }
    const missingActs = selectedActivities.filter(
      act => !hasClientRepSig(activityCustomerRepSigByActivity[act])
    );
    if (missingActs.length > 0) {
      return `Client representative signature required for: ${missingActs.join(', ')}`;
    }
    return '';
  };

  // Build activities payload for API
  const buildActivitiesPayload = () => {
    const activitiesPayload: Record<string, { status: string; accepted: boolean; comments: string; qcSignature?: any; customerSignature?: any }> = {};

    // Only include selected activities
    selectedActivities.forEach(activity => {
      const state = activityStates[activity];
      const apiKey = ACTIVITY_TO_API_KEY[activity];
      const activityCustomerRep = activityCustomerRepSigByActivity[activity] ?? null;
      
      if (apiKey && state) {
        activitiesPayload[apiKey] = {
          status: state.status || 'na',
          accepted: state.status === 'accepted',
          comments: state.comments || '',
          // Use the final QC signature for all activities
          qcSignature: ptQCSignature ? {
            name: ptQCSignature.name || '',
            timestamp: ptQCSignature.timestamp || new Date().toISOString(),
            signature: ptQCSignature.signature || '',
            stampImage: ''
          } : undefined,
          customerSignature: activityCustomerRep ? {
            name: activityCustomerRep.name || '',
            timestamp: activityCustomerRep.timestamp || new Date().toISOString(),
            signature: activityCustomerRep.signature || '',
            stampImage: activityCustomerRep.stampImage || ''
          } : undefined
        };
      }
    });
    
    return activitiesPayload;
  };

  // Handle generate report
  const handleGenerateReport = () => {
    // if (!isFormValid()) return;

    // Build serial numbers array - deduplicate to avoid sending duplicates
    const uniqueAssemblies = Array.from(new Set(selectedAssemblies));
    const uniqueSerialNumbers = Array.from(new Set(serialNumbers.filter(s => s.trim())));
    const serialNumbersArray = assemblyOptionsForUi.length > 0 
      ? uniqueAssemblies.map(n => ({ assemblyNumber: n }))
      : uniqueSerialNumbers.map(n => ({ assemblyNumber: n }));

    // Extract pipeId and projectTypeId from selectedPipe if available, else use props

    // Build the API payload
    const payload: any = {
      projectId: projectId,
      pipeId: selectedPipe?.pipeId,
      projectTypeId: selectedPipe?.projectTypeId,
      clientContractNo: headerData.clientContractNo,
      location: headerData.location,
      description: headerData.description,
      quantity: serialNumbersArray.length.toString(),
      serialNumbers: serialNumbersArray,
      activities: buildActivitiesPayload(),
      remarks: remarks,
      ptQcSignature: ptQCSignature ? {
        name: ptQCSignature.name || '',
        timestamp: ptQCSignature.timestamp || new Date().toISOString(),
        signature: ptQCSignature.signature || '',
        stampImage: ptQCSignature.stampImage || ''
      } : undefined,
      projectManagerSignature: projectManagerSignature ? {
        name: projectManagerSignature.name || '',
        timestamp: projectManagerSignature.timestamp || new Date().toISOString(),
        signature: projectManagerSignature.signature || '',
        stampImage: projectManagerSignature.stampImage || ''
      } : undefined,
      // customerRepSignature: (() => {
      //   const typeActs = ACTIVITIES_BY_TYPE[selectedType] || [];
      //   const first = typeActs.find(
      //     a => a !== 'Welding' && a !== 'Coating' && selectedActivities.includes(a)
      //   );
      //   const sig = first ? activityCustomerRepSigByActivity[first] : null;
      //   return sig?.signature
      //     ? {
      //         name: sig.name || '',
      //         timestamp: sig.timestamp || new Date().toISOString(),
      //         signature: sig.signature || '',
      //         stampImage: sig.stampImage || ''
      //       }
      //     : undefined;
      // })(),
      // weldingCustomerRepSignature:
      //   selectedActivities.includes('Welding') && activityCustomerRepSigByActivity['Welding']?.signature
      //     ? {
      //         name: activityCustomerRepSigByActivity['Welding']!.name || '',
      //         timestamp:
      //           activityCustomerRepSigByActivity['Welding']!.timestamp || new Date().toISOString(),
      //         signature: activityCustomerRepSigByActivity['Welding']!.signature || '',
      //         stampImage: activityCustomerRepSigByActivity['Welding']!.stampImage || ''
      //       }
      //     : undefined,
      // coatingCustomerRepSignature:
      //   selectedActivities.includes('Coating') && activityCustomerRepSigByActivity['Coating']?.signature
      //     ? {
      //         name: activityCustomerRepSigByActivity['Coating']!.name || '',
      //         timestamp:
      //           activityCustomerRepSigByActivity['Coating']!.timestamp || new Date().toISOString(),
      //         signature: activityCustomerRepSigByActivity['Coating']!.signature || '',
      //         stampImage: activityCustomerRepSigByActivity['Coating']!.stampImage || ''
      //       }
      //     : undefined
    };

    // Add email addresses if provided
    // if (emailList.length > 0) {
    //   payload.emailAddress = emailList;
    // }

    // Dispatch the Redux action
    dispatch(generateIRNReport(payload));
  };

  const activities = ACTIVITIES_BY_TYPE[selectedType] || [];

  // If viewer is open, render only the viewer
  if (viewerFile) {
    return (
      <PdfViewer
        fileUrl={viewerFile.fileUrl}
        fileName={viewerFile.fileName}
        onClose={closeViewer}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden"
      >
        <div className="flex flex-col relative z-20 h-screen overflow-hidden">
          {/* Header */}
          <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div>
                <DialogTitle className="text-lg font-bold text-gray-900">
                  Inspection Release Note (IRN)
                </DialogTitle>
                {headerInfoItems.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {headerInfoItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs font-medium`}>
                        {item.label}: {item.value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 relative">
            {/* Blur overlay with loader */}
            {(isFetchingDraft || isReloading) && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-lg">
                <Loader2 className="h-8 w-8 animate-spin text-[#EB1C37] mb-4" />
                <span className="text-sm text-gray-600">{isReloading ? 'Reloading draft data...' : 'Loading draft data...'}</span>
              </div>
            )}
            {/* Form content - always visible, disabled during loading */}
            <div className={cn("space-y-8", (isFetchingDraft || isReloading) && "pointer-events-none opacity-50")}>
             
              {/* Section 1: IRN Type Selection - Only show if not hidden */}
              {!hideTypeSelection && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    1. Select IRN Type
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(TYPE_LABELS).map(([value, label]) => (
                      <label
                        key={value}
                        className={cn(
                          "flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all",
                          selectedType === value
                            ? "border-[#EB1C37] bg-red-50"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        <input
                          type="radio"
                          name="irnType"
                          value={value}
                          checked={selectedType === value}
                          onChange={(e) => {
                            setSelectedType(e.target.value);
                            if (!isApplyingDraftRef.current) {
                              setIsDirty(true);
                            }
                          }}
                          className="h-4 w-4 text-[#EB1C37] focus:ring-[#EB1C37] accent-[#EB1C37]"
                        />
                        <span className="text-sm font-medium text-gray-900">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Section 2: Header Fields */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  {hideTypeSelection ? '1' : '2'}. Release Note Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientContractNo">Client Contract No <span className="text-red-500">*</span></Label>
                    <Input
                      id="clientContractNo"
                      value={headerData.clientContractNo}
                      onChange={(e) => handleHeaderChange('clientContractNo', e.target.value)}
                      placeholder="e.g., 129010019"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location <span className="text-red-500">*</span></Label>
                    <Input
                      id="location"
                      value={headerData.location}
                      onChange={(e) => handleHeaderChange('location', e.target.value)}
                      placeholder="e.g., 6605 Rankin Road, Humble, Texas 77396"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">Description *</Label>
                    <textarea
                      id="description"
                      value={headerData.description}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleHeaderChange('description', e.target.value)}
                      placeholder="e.g., 18&quot;O.D.x 0.75&quot; W.T Buckle Arrestor"
                      rows={2}
                      className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Serial Numbers */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  {hideTypeSelection ? '2' : '3'}. Serial Numbers
                </h3>
                
                {assemblyOptionsForUi.length > 0 || (hideTypeSelection && pipeAssembliesLoading) ? (
                  /* Assembly checkboxes when assemblies prop is provided */
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Select Assemblies (Serial Numbers) *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllAssemblies}
                        disabled={hideTypeSelection && pipeAssembliesLoading}
                      >
                        {selectedAssemblies.length === assemblyOptionsForUi.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500">
                      {hideTypeSelection && pipeAssembliesLoading
                        ? 'Loading assemblies…'
                        : `${selectedAssemblies.length} of ${assemblyOptionsForUi.length} assemblies selected`}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto border rounded-lg p-3 bg-gray-50">
                      {hideTypeSelection && pipeAssembliesLoading && assemblyOptionsForUi.length === 0 ? (
                        <div className="col-span-full flex items-center justify-center gap-2 py-8 text-sm text-gray-600">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Loading assemblies…
                        </div>
                      ) : null}
                      {assemblyOptionsForUi.map((assembly) => {
                        const isSelected = selectedAssemblies.includes(assembly.assemblyNumber);
                        return (
                          <div
                            key={assembly.assemblyNumber}
                            onClick={() => handleAssemblyToggle(assembly.assemblyNumber)}
                            className={cn(
                              "flex items-center gap-2 p-2 border rounded cursor-pointer transition-all",
                              isSelected
                                ? "border-[#EB1C37] bg-red-100 text-red-900 font-semibold"
                                : "border-gray-300 hover:border-[#EB1C37] hover:bg-gray-100 bg-white"
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleAssemblyToggle(assembly.assemblyNumber)}
                              className="h-4 w-4 text-[#EB1C37] focus:ring-[#EB1C37] accent-[#EB1C37]"
                            />
                            <span className="text-xs font-medium truncate">
                              {assembly.assemblyNumber}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  /* Manual serial number input when no assemblies provided */
                  <div className="space-y-3">
                    <Label>Serial Numbers *</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {serialNumbers.map((serial, index) => (
                        <div key={index} className="flex items-center gap-1">
                          <Input
                            value={serial}
                            onChange={(e) => handleSerialNumberChange(index, e.target.value)}
                            placeholder={`18 x .75 -${(index + 1).toString().padStart(3, '0')}`}
                            className="text-sm"
                          />
                          {serialNumbers.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSerialNumber(index)}
                              className="p-1 h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSerialNumber}
                      className="mt-2"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Serial Number
                    </Button>
                  </div>
                )}
              </div>

              {/* Section 4: Activities */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  {hideTypeSelection ? '3' : '4'}. Activities
                </h3>
                
                {/* Activity Selection Header */}
                  <div className="flex items-center justify-between">
                  <div>
                    <Label>Select Activities *</Label>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedActivities.length} of {activities.length} activities selected
                    </p>
                  </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllActivities}
                    >
                      {selectedActivities.length === activities.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                {/* Unified Activities Table — each row includes its own client rep signature panel */}
                <div className="border rounded-lg overflow-x-auto w-full">
                  <table className="w-full min-w-[920px] table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900 w-[18%]">Activity</th>
                        <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900 w-[14%]">Assessment</th>
                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900 w-[30%]">Comments</th>
                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900 w-[38%]">Client representative</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                    {activities.map((activity) => {
                      const isSelected = selectedActivities.includes(activity);
                        const state = activityStates[activity] || { status: null, comments: '' };
                        const repMember = activityCustomerRepMemberByActivity[activity] ?? '';
                        const repSig = activityCustomerRepSigByActivity[activity];
                      return (
                          <tr key={activity} className={cn(
                            "hover:bg-gray-50 transition-colors",
                            isSelected ? "bg-red-50/30" : ""
                          )}>
                            <td className="px-3 py-3 align-top">
                              <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleActivityToggle(activity)}
                            className="h-4 w-4 mt-1 text-[#EB1C37] focus:ring-[#EB1C37] accent-[#EB1C37]"
                          />
                                <span className={cn(
                                  "text-sm font-medium pt-0.5",
                                  isSelected ? "text-gray-900" : "text-gray-600"
                                )}>
                                  {activity}
                                </span>
                        </label>
                              </td>
                              <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                  <button
                                    type="button"
                                  onClick={() => handleActivityStatus(activity, state.status === 'accepted' ? null : 'accepted')}
                                    className={cn(
                                      "p-2 rounded transition-colors",
                                    state.status === 'accepted'
                                        ? "bg-green-100 text-green-600"
                                        : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                                    )}
                                    aria-label="Accept"
                                  title="Accept"
                                  >
                                  <ThumbsUp className="h-5 w-5" />
                                  </button>
                                  <button
                                    type="button"
                                  onClick={() => handleActivityStatus(activity, state.status === 'rejected' ? null : 'rejected')}
                                    className={cn(
                                      "p-2 rounded transition-colors",
                                    state.status === 'rejected'
                                        ? "bg-red-100 text-red-600"
                                        : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                                    )}
                                    aria-label="Reject"
                                  title="Reject"
                                  >
                                  <ThumbsDown className="h-5 w-5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleActivityStatus(activity, state.status === 'na' ? null : 'na')}
                                  className={cn(
                                    "px-2 py-1 rounded transition-colors text-sm font-semibold min-w-[36px]",
                                    state.status === 'na'
                                      ? "bg-gray-600 text-white"
                                      : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                                  )}
                                  aria-label="Not Applicable"
                                  title="Not Applicable"
                                >
                                  NA
                                  </button>
                                </div>
                              </td>
                              <td className="px-3 py-3 align-top">
                                <Input
                                  value={state.comments}
                                  onChange={(e) => handleActivityCommentChange(activity, e.target.value)}
                                  placeholder="Enter comments..."
                                  className="text-sm"
                                />
                              </td>
                              <td className="px-3 py-3 align-top min-w-[240px]">
                                <div className="space-y-2">
                                  <div className="flex w-full items-center gap-2">
                                    <div className="min-w-0 flex-1">
                                      <Select
                                        value={repMember}
                                        onValueChange={(v) => setActivityCustomerRepMember(activity, v)}
                                      >
                                        <SelectTrigger className="h-9 w-full text-xs">
                                          <SelectValue placeholder="Select client representative" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {customerRepList.length > 0 ? (
                                            customerRepList.map(member => (
                                              <SelectItem key={member.value} value={member.value}>
                                                {member.label}
                                              </SelectItem>
                                            ))
                                          ) : (
                                            <SelectItem value="none" disabled>
                                              No client representatives available
                                            </SelectItem>
                                          )}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {repMember ? (
                                      <div className="ml-auto flex shrink-0 items-center justify-end gap-1">
                                        <Button
                                          type="button"
                                          //variant="outline"
                                          size="sm"
                                          className="h-9 whitespace-nowrap px-2.5 text-xs"
                                          onClick={() => openActivityCustomerRepSignatureDialog(activity)}
                                        >
                                          <FileSignature className="h-3.5 w-3.5 mr-1" />
                                          Add signature
                                        </Button>
                                        {repSig?.signature ? (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-9 w-9 shrink-0 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                            title="Remove signature"
                                            onClick={() => {
                                              setActivityCustomerRepSigByActivity(prev => ({
                                                ...prev,
                                                [activity]: null,
                                              }));
                                              if (!isApplyingDraftRef.current) {
                                                setIsDirty(true);
                                              }
                                            }}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                  {repSig?.signature ? (
                                    <div className="rounded-md border bg-gray-50 p-2 text-xs space-y-1.5">
                                      <div className="flex justify-between gap-2">
                                        <span className="text-gray-500 shrink-0">Signed by</span>
                                        <span className="font-medium text-right truncate">{repSig.name}</span>
                                      </div>
                                      <div className="text-gray-500">{formatDate(repSig.timestamp)}</div>
                                      {repSig.comments ? (
                                        <p className="text-gray-700 leading-snug">{repSig.comments}</p>
                                      ) : null}
                                      <div className="flex flex-wrap gap-2 rounded border bg-white p-1.5">
                                        <img
                                          src={repSig.signature}
                                          alt={`${activity} client signature`}
                                          className="max-h-12 w-auto"
                                        />
                                        {repSig.stampImage ? (
                                          <img
                                            src={repSig.stampImage}
                                            alt={`${activity} stamp`}
                                            className="max-h-12 w-auto"
                                          />
                                        ) : null}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
              </div>

              {/* Section 5: Remarks */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  {hideTypeSelection ? '4' : '5'}. Remarks
                </h3>
                <textarea
                  value={remarks}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { 
                    setRemarks(e.target.value); 
                    if (!isApplyingDraftRef.current) {
                      setIsDirty(true);
                    }
                  }}
                  placeholder="Enter any additional remarks..."
                  rows={3}
                  className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              {/* Section 6: Email Addresses - Commented out for now */}
              {/* <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  {hideTypeSelection ? '5' : '6'}. Email Notification (Optional)
                </h3>
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">
                    Email Addresses
                    <span className="ml-2 text-xs text-gray-500 font-normal">
                      Add multiple emails to receive the report
                    </span>
                  </Label>

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Input
                          type="email"
                          placeholder="Enter email address and press Enter or click Add"
                          value={email}
                          onChange={handleEmailChange}
                          onKeyPress={handleEmailKeyPress}
                          className={cn(
                            emailError ? '' : ''
                          )}
                          disabled={irnReportLoading || irnReportSuccess}
                        />
                        {emailError && (
                          <p className="text-xs text-red-600 mt-1">{emailError}</p>
                        )}
                      </div>
                      <Button
                        onClick={handleAddEmail}
                        size="sm"
                        variant="outline"
                        className="h-10 px-4"
                        disabled={!email.trim() || irnReportLoading || irnReportSuccess}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>

                    {emailList.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        {emailList.map((emailAddr, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-1 px-3 py-1.5 text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-md text-sm"
                          >
                            <span>{emailAddr}</span>
                            {!irnReportLoading && !irnReportSuccess && (
                              <button
                                onClick={() => handleRemoveEmail(emailAddr)}
                                className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {emailList.length === 0 && (
                      <p className="text-xs text-gray-500">
                        No email addresses added. Report will be generated without email notification.
                      </p>
                    )}
                  </div>
                </div>
              </div> */}

              {/* Section 6: Final Signatures (QC + PM; client reps are per activity in the table above) */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  {hideTypeSelection ? '5' : '6'}. Signatures
                </h3>
                <p className="text-sm text-gray-500">
                  QC Inspector and Project Manager signatures are required before generating the report. Each activity you select must have its own client representative signature; activities you do not select are not validated.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* QC Inspector Signature */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>QC Inspector</Label>
                        <div className="flex gap-2">
                          <Select value={selectedPtQC} onValueChange={setSelectedPtQC}>
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select QC Inspector" />
                            </SelectTrigger>
                            <SelectContent>
                              {qcList.length > 0 ? (
                                qcList.map(member => (
                                  <SelectItem key={member.value} value={member.value}>
                                    {member.label}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="none" disabled>
                                  No QC Inspectors available
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          {selectedPtQC && (
                            <div className="flex gap-2">
                              { (
                                <Button
                                  type="button"
                                  onClick={() => openFinalSignatureDialog('pt-qc')}
                                  //variant="outline"
                                  //size="sm"
                                  className="flex items-center gap-2 whitespace-nowrap"
                                >
                                  <FileSignature className="h-4 w-4" />
                                  Add Signature
                                </Button>
                              )}
                              {ptQCSignature && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => { 
                                    setPtQCSignature(null); 
                                    if (!isApplyingDraftRef.current) {
                                      setIsDirty(true);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                                  title="Remove Signature"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {ptQCSignature && ptQCSignature.signature && (
                          <div className="p-3 border rounded-md bg-gray-50 space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Signed by:</span>
                              <span className="text-sm font-medium">{ptQCSignature.name}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Date:</span>
                              <span className="text-sm">{formatDate(ptQCSignature.timestamp)}</span>
                            </div>
                            {ptQCSignature.comments && (
                              <div>
                                <span className="text-sm text-gray-500">Comment:</span>
                                <p className="text-sm mt-1">{ptQCSignature.comments}</p>
                              </div>
                            )}
                            <div className="border rounded p-2 bg-white flex items-center justify-between gap-4">
                              <img
                                src={ptQCSignature.signature}
                                alt="QC Inspector Signature"
                                className="max-h-16 w-auto"
                              />
                              {ptQCSignature.stampImage && (
                                <img
                                  src={ptQCSignature.stampImage}
                                  alt="QC Inspector Stamp"
                                  className="max-h-16 w-auto"
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Project Manager Signature */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Project Manager</Label>
                        <div className="flex gap-2">
                          <Select value={selectedProjectManager} onValueChange={setSelectedProjectManager}>
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select Project Manager" />
                            </SelectTrigger>
                            <SelectContent>
                              {pmList.length > 0 ? (
                                pmList.map(member => (
                                  <SelectItem key={member.value} value={member.value}>
                                    {member.label}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="none" disabled>
                                  No Project Managers available
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          {selectedProjectManager && (
                            <div className="flex gap-2">
                              { (
                                <Button
                                  type="button"
                                  onClick={() => openFinalSignatureDialog('project-manager')}
                                //      variant="outline"
                                 // size="sm"
                                  className="flex items-center gap-2 whitespace-nowrap"
                                >
                                  <FileSignature className="h-4 w-4" />
                                  Add Signature
                                </Button>
                              )}
                              {projectManagerSignature && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => { 
                                    setProjectManagerSignature(null); 
                                    if (!isApplyingDraftRef.current) {
                                      setIsDirty(true);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                                  title="Remove Signature"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {projectManagerSignature && projectManagerSignature.signature && (
                          <div className="p-3 border rounded-md bg-gray-50 space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Signed by:</span>
                              <span className="text-sm font-medium">{projectManagerSignature.name}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Date:</span>
                              <span className="text-sm">{formatDate(projectManagerSignature.timestamp)}</span>
                            </div>
                            {projectManagerSignature.comments && (
                              <div>
                                <span className="text-sm text-gray-500">Comment:</span>
                                <p className="text-sm mt-1">{projectManagerSignature.comments}</p>
                              </div>
                            )}
                            <div className="border rounded p-2 bg-white flex items-center justify-between gap-4">
                              <img
                                src={projectManagerSignature.signature}
                                alt="Project Manager Signature"
                                className="max-h-16 w-auto"
                              />
                              {projectManagerSignature.stampImage && (
                                <img
                                  src={projectManagerSignature.stampImage}
                                  alt="Project Manager Stamp"
                                  className="max-h-16 w-auto"
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Success Message */}
              {irnReportSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div className="text-sm text-green-700">
                    <span>IRN Report generated successfully!</span>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {irnReportError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <div className="text-sm text-red-700">
                    <div className="font-medium">Error: {irnReportError}</div>
                  </div>
                </div>
              )}

              {/* Draft Status Info */}
              {(saveDraftSuccess || saveDraftError || missingFields.length > 0) && (
                <div className="space-y-2">
                  {/* {saveDraftSuccess && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border rounded-lg">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                      <div className="text-sm text-blue-700">
                        <span>Draft saved successfully!</span>
                      </div>
                    </div>
                  )} */}
                  {saveDraftError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border rounded-lg">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <div className="text-sm text-red-700">
                        <span>Failed to save draft: {saveDraftError}</span>
                      </div>
                    </div>
                  )}
                  {missingFields.length > 0 && !isReadyToGenerate && (
                    <div className="flex items-start gap-2 p-3 bg-yellow-50 border rounded-lg">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-700">
                        <span className="font-medium">Missing required fields:</span>
                        <ul className="list-disc list-inside mt-1">
                          {missingFields.map((field: string, index: number) => (
                            <li key={index}>{field}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t">
                {/* <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="px-6"
                  disabled={irnReportLoading || isSavingDraft}
                >
                  {irnReportSuccess ? 'Close' : 'Cancel'}
                </Button> */}
                { (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSaveDraft}
                      disabled={isSavingDraft || irnReportLoading || !isDirty}
                      className="px-6"
                      title={
                        !isDirty
                          ? 'No changes to save — edit any field to enable'
                          : !isDraftValid()
                            ? 'Tip: add Client Contract No, Location, at least one serial/assembly, and one activity for a complete draft (save still allowed)'
                            : ''
                      }
                    >
                      {isSavingDraft ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Draft
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleGenerateReport}
                      disabled={!canGenerate || irnReportLoading }
                      className="bg-[#EB1C37] hover:bg-[#EB1C37]/90 text-white px-6"
                      title={getGenerateButtonTooltip()}
                    >
                      {irnReportLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FileSignature className="h-4 w-4 mr-2" />
                          Generate Report
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Signature Dialog */}
      <SignatureDialog
        isOpen={isSignatureDialogOpen}
        onClose={() => {
          setIsSignatureDialogOpen(false);
          setSignatureDialogContext(null);
        }}
        onSave={handleSignatureSave}
        title={
          signatureDialogContext?.type === 'pt-qc' 
            ? 'QC Inspector Signature' 
            : signatureDialogContext?.type === 'project-manager'
            ? 'Project Manager Signature'
            : 'Customer Representative Signature'
        }
        initialName={signatureDialogContext?.initialName || ''}
      />
    </Dialog>
  );
};

